import { updatePendingThreadCount } from 'actions/messaging'
import AddUserIcon from 'components/ReusableComponents/Icons/AddUser'
import AnalyticsIcon from 'components/ReusableComponents/Icons/Analytics'
import AnnouncementIcon from 'components/ReusableComponents/Icons/Announcement'
import ArrivalsIcon from 'components/ReusableComponents/Icons/Arrivals'
import BeforeStayIcon from 'components/ReusableComponents/Icons/BeforeStay'
import OccupancyIcon from 'components/ReusableComponents/Icons/CalendarDotted'
import CampaignAnalysisIcon from 'components/ReusableComponents/Icons/CampaignAnalysis'
import ChatIcon from 'components/ReusableComponents/Icons/Chat'
import ChatBotIcon from 'components/ReusableComponents/Icons/ChatBot'
import ComplianceIcon from 'components/ReusableComponents/Icons/Compliance'
import ContractIcon from 'components/ReusableComponents/Icons/Contract'
import DirectMessage from 'components/ReusableComponents/Icons/DirectMessage'
import ExclamationIcon from 'components/ReusableComponents/Icons/Exclamation'
import FeedbackIcon from 'components/ReusableComponents/Icons/Feedback'
import HotelSetupIcon from 'components/ReusableComponents/Icons/HotelSetup'
import InPropertyIcon from 'components/ReusableComponents/Icons/InProperty'
import InviteIcon from 'components/ReusableComponents/Icons/Invite'
import InvoiceIcon from 'components/ReusableComponents/Icons/Invoice'
import MapIcon from 'components/ReusableComponents/Icons/Map'
import GraphicIcon from 'components/ReusableComponents/Icons/MessagingPerformance'
import MonthlyRevenue from 'components/ReusableComponents/Icons/MonthlyRevenue'
import NotificationIcon from 'components/ReusableComponents/Icons/Notification'
import OtaBusterIcon from 'components/ReusableComponents/Icons/OtaBuster'
import ReservationIcon from 'components/ReusableComponents/Icons/Reservation'
import RestrictedDateIcon from 'components/ReusableComponents/Icons/RestrictedDate'
import ReviewsIcon from 'components/ReusableComponents/Icons/Reviews'
import ROIIcon from 'components/ReusableComponents/Icons/ROI'
import SiteAbandonment from 'components/ReusableComponents/Icons/SiteAbandonment'
import TextBlastIcon from 'components/ReusableComponents/Icons/TextBlast'
import UsageStatsIcon from 'components/ReusableComponents/Icons/UsageStats'
import UserManagementIcon from 'components/ReusableComponents/Icons/UserManagement'
import SidebarDirectfulTestLogo from 'components/ReusableComponents/SidebarDirectfulTester'
import { userPositions } from 'features/ManageUserDialog/userTitleTypes'
import { userRoleLevels, userRoles } from 'features/UserPermissions'
import { useInternalAdmin } from 'hooks'
import useResortNearDisney from 'hooks/useResortNearDisney'
import { PMSs } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useHistory, useLocation } from 'react-router-dom'

import { selectCurrentHotelPMS, selectIsInternalAdmin } from '../accessors/index'
import CurrentHotelWithSelector from './ReusableComponents/CurrentHotelWithSelector/index'
import CSMListIcon from './ReusableComponents/Icons/CSMList'
import DefaultCampaignsIcon from './ReusableComponents/Icons/DefaultCampaignsIcon'
import EmailIcon from './ReusableComponents/Icons/EmailIcon'
import { HealthIcon } from './ReusableComponents/Icons/Health'
import UploadReportIcon from './ReusableComponents/Icons/Upload'
import { SIDEBAR_NAV_MODES } from './sidebarNavMode'
import useSidebarNavMode from './useSidebarNavMode'

function getSidebarLinkIsActive(to) {
  return (match, location) => {
    if (!match) {
      return false
    }

    if (typeof to === 'string' && to.includes('?')) {
      return `${location.pathname}${location.search}` === to
    }

    return !!match
  }
}

const Link = ({
  children,
  to,
  adminOnly = false,
  devOnly = false,
  operaOnly = false,
  icon
}) => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const pms = useSelector(selectCurrentHotelPMS)

  if (devOnly && !document.location.host.match(/dev-|localhost/)) {
    return null
  }

  if (adminOnly && !isInternalAdmin) {
    return null
  }

  if (operaOnly && pms !== 'opera') {
    return null
  }

  if (typeof icon === 'string') {
    return (
      <li>
        <NavLink to={to} isActive={getSidebarLinkIsActive(to)}>
          <i className={icon} data-tip={children}></i>
          <span>{children}</span>
        </NavLink>
      </li>
    )
  }

  const Icon = icon

  return (
    <li>
      <NavLink to={to} isActive={getSidebarLinkIsActive(to)}>
        {Icon && <Icon className="sidebar-svg-icon" />}
        <span>{children}</span>
      </NavLink>
    </li>
  )
}

const SidebarFunc = ({ noCollapse }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [navMode, setNavMode] = useSidebarNavMode()

  const roles = useSelector(state => state.login.roles)
  const position = useSelector(state => state.login.position)

  const localVersion = useSelector(s => s.versionInfo.localVersion)

  const pendingSalesThreadsCountFromApi = useSelector(
    s => s.salesMessaging.pendingThreadsCount
  )
  const salesThreads = useSelector(s => s.salesMessaging.threads)
  const pendingSalesThreadsCount =
    Object.keys(salesThreads || {}).length > 0
      ? Object.values(salesThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingSalesThreadsCountFromApi
  const pendingGdsThreadsCount = useSelector(s => s.salesMessaging.pendingGdsThreadsCount)
  const pendingBookingCount = useSelector(s => s.salesMessaging.pendingBookingCount)

  const isInviteBackDisabled = useSelector(s => s.hotelDetails.inviteBackIsDisabled)

  const isIndependentlyManaged = useSelector(s => s.hotelDetails.groupId) === -100

  const isPreOnboardingCompleted = useSelector(
    s => s.hotelDetails.isPreOnboardingCompleted
  )

  const pendingServiceThreadsCountFromApi = useSelector(
    s => s.serviceMessaging.pendingThreadsCount
  )
  const serviceThreads = useSelector(s => s.serviceMessaging.threads)
  const pendingServiceThreadsCount =
    Object.keys(serviceThreads || {}).length > 0
      ? Object.values(serviceThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingServiceThreadsCountFromApi

  const pendingReviewThreadsCountFromApi = useSelector(
    s => s.reviewMessaging.pendingThreadsCount
  )
  const reviewThreads = useSelector(s => s.reviewMessaging.threads)
  const pendingReviewThreadsCount =
    Object.keys(reviewThreads || {}).length > 0
      ? Object.values(reviewThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingReviewThreadsCountFromApi

  const pendingDirectThreadsCountFromApi = useSelector(
    s => s.directMessaging.pendingThreadsCount
  )
  const directThreads = useSelector(s => s.directMessaging.threads)
  const pendingDirectThreadsCount =
    Object.keys(directThreads || {}).length > 0
      ? Object.values(directThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingDirectThreadsCountFromApi

  const isInternalAdmin = useInternalAdmin()

  const pms = useSelector(state => state.hotelDetails.pms)

  const hasDisconnectedPMS = pms === PMSs.disconnected
  const hasPepPMS = pms === PMSs.pep

  function canSeePage(role) {
    const positivePermissions = [
      userRoleLevels.HasGroupLevelPermission,
      userRoleLevels.HasPermission
    ]

    return positivePermissions.includes(roles[role])
  }

  const hasMessagingAccess =
    canSeePage(userRoles.ChatInProperty) ||
    canSeePage(userRoles.ChatSalesBefore) ||
    canSeePage(userRoles.ChatSalesAfter) ||
    canSeePage(userRoles.ChatDirect)

  function canSeeROIPage() {
    if (isInternalAdmin) {
      return true
    }
    if (!isIndependentlyManaged) {
      return roles[userRoles.Billing] === userRoleLevels.HasGroupLevelPermission
    }
    if (isIndependentlyManaged) {
      return (
        [userPositions.Corporate, userPositions.GeneralManager].includes(position) &&
        roles[userRoles.Billing] === userRoleLevels.HasPermission
      )
    }
    return false
  }

  const hasRoiAccess = canSeeROIPage()

  const dispatch = useDispatch()

  useEffect(() => {
    if (hasMessagingAccess) {
      dispatch(updatePendingThreadCount())
    }
  }, [dispatch, hasMessagingAccess])

  const history = useHistory()
  const location = useLocation()

  const isResortNearDisney = useResortNearDisney()
  const isNewNav = navMode === SIDEBAR_NAV_MODES.new

  function handleSwitchToClassic() {
    setNavMode(SIDEBAR_NAV_MODES.old)
  }

  function handleSwitchToNew() {
    setNavMode(SIDEBAR_NAV_MODES.new)
  }

  const inviteWarningIcon = isInviteBackDisabled && (
    <ExclamationIcon
      style={{
        width: 19,
        height: 19,
        position: 'absolute',
        right: 6,
        background: 'white',
        borderRadius: '50%',
        fill: '#ff5e5e',
        marginTop: 8
      }}
    />
  )

  function renderArrivalsLink() {
    if (hasDisconnectedPMS || isResortNearDisney) {
      return null
    }

    return (
      <Link to="/arrivals" icon={ArrivalsIcon}>
        Arrivals &amp; In-house
      </Link>
    )
  }

  function renderBookingsLinks() {
    return (
      <>
        {!isResortNearDisney && (
          <Link to="/bookings" icon={ReservationIcon}>
            Bookings
          </Link>
        )}
        {isResortNearDisney && (
          <Link to="/gds-bookings" icon={ReservationIcon}>
            Bookings
          </Link>
        )}
        {!isResortNearDisney && (
          <Link to="/gds-bookings" icon={ReservationIcon} adminOnly>
            GDS bookings
            <small
              className="main-sidebar-count-bubble"
              style={{
                background: pendingBookingCount > 0 ? '#ee362c' : '#0079ff'
              }}
            >
              {pendingBookingCount?.toString()}
              <span
                style={{
                  opacity: 0.5,
                  fontSize: 11,
                  marginTop: -2,
                  width: 10,
                  textAlign: 'center'
                }}
              >
                |
              </span>
              {pendingGdsThreadsCount?.toString()}
            </small>
          </Link>
        )}
      </>
    )
  }

  function renderRecentSubscriptionsLink() {
    if (!hasDisconnectedPMS) {
      return null
    }

    return (
      <Link to="/subscribers" icon={AddUserIcon}>
        Recent subscriptions
      </Link>
    )
  }

  function renderSiteAbandonmentLink() {
    return (
      <Link to="/site-abandonment" icon={SiteAbandonment}>
        Site abandonment
      </Link>
    )
  }

  function renderOtaBusterLink() {
    return (
      <Link to="/ota-buster" icon={OtaBusterIcon} adminOnly>
        OTA Buster
      </Link>
    )
  }

  function renderDuringStayLink() {
    if (!canSeePage(userRoles.ChatInProperty)) {
      return null
    }

    return (
      <Link to="/messaging" icon={ChatIcon}>
        {isNewNav ? 'Messaging' : 'During stay'}
        {pendingServiceThreadsCount > 0 && (
          <small className="main-sidebar-count-bubble">
            {pendingServiceThreadsCount}
          </small>
        )}
      </Link>
    )
  }

  function renderBeforeStayLink() {
    if (!canSeePage(userRoles.ChatSalesBefore)) {
      return null
    }

    return (
      <Link to="/incoming" icon={BeforeStayIcon}>
        Before stay
        {pendingSalesThreadsCount > 0 && (
          <small className="main-sidebar-count-bubble">
            {pendingSalesThreadsCount}
          </small>
        )}
      </Link>
    )
  }

  function renderGuestResponsesLink() {
    if (!canSeePage(userRoles.ChatSalesAfter)) {
      return null
    }

    return (
      <Link to="/feedback" icon={FeedbackIcon}>
        {isNewNav ? 'Guest responses' : 'Reviews'}
        {pendingReviewThreadsCount > 0 && (
          <small className="main-sidebar-count-bubble">
            {pendingReviewThreadsCount}
          </small>
        )}
      </Link>
    )
  }

  function renderDirectMessagesLink() {
    if (!canSeePage(userRoles.ChatDirect)) {
      return null
    }

    return (
      <Link to="/direct" icon={DirectMessage}>
        Direct messages
        {pendingDirectThreadsCount > 0 && (
          <small className="main-sidebar-count-bubble">
            {pendingDirectThreadsCount}
          </small>
        )}
      </Link>
    )
  }

  function renderChatbotLink() {
    if (!canSeePage(userRoles.ChatInProperty)) {
      return null
    }

    return (
      <Link to="/chatbot-responses" icon={ChatBotIcon}>
        {isNewNav ? 'Chatbot' : 'Chatbot responses'}
      </Link>
    )
  }

  function renderAnalyticsLink() {
    if (!canSeePage(userRoles.ReportsView)) {
      return null
    }

    return (
      <Link to="/analytics" icon={AnalyticsIcon}>
        Analytics
      </Link>
    )
  }

  function renderAnalyticsSubNav() {
    if (
      !location.pathname.includes('/analytics') ||
      isResortNearDisney ||
      !canSeePage(userRoles.ReportsView)
    ) {
      return null
    }

    return (
      <div className="sub-navigation">
        <Link to="/analytics/subscriptions">Subscriptions</Link>
        <Link to="/analytics/outreach">Outreach</Link>
        <Link to="/analytics/OTA-outreach">OTA Outreach</Link>
        {/* <Link to="/analytics/ota-leakage">OTA Leakage</Link> */}
        <Link to="/analytics/reviews">Reviews</Link>
        <Link to="/analytics/conversions">Conversions</Link>
      </div>
    )
  }

  function renderRoiLink() {
    if (!hasRoiAccess) {
      return null
    }

    return (
      <Link to="/roi" icon={ROIIcon}>
        Return on investment
      </Link>
    )
  }

  function renderReviewRatingsLink() {
    if (isResortNearDisney || !canSeePage(userRoles.ReportsView)) {
      return null
    }

    return (
      <Link to="/reviews" icon={ReviewsIcon}>
        Review ratings
      </Link>
    )
  }

  function renderMessagingPerformanceLink() {
    if (
      !(
        roles[userRoles.Billing] === userRoleLevels.HasGroupLevelPermission ||
        roles[userRoles.BookingsView] === userRoleLevels.HasGroupLevelPermission ||
        roles[userRoles.BookingsManage] === userRoleLevels.HasGroupLevelPermission ||
        roles[userRoles.UserManage] === userRoleLevels.HasGroupLevelPermission ||
        isInternalAdmin
      ) ||
      isIndependentlyManaged
    ) {
      return null
    }

    return (
      <Link to="/messaging-performance" icon={GraphicIcon}>
        Messaging Performance
      </Link>
    )
  }

  function renderInviteMessagesLink() {
    return (
      <Link
        to={
          isNewNav
            ? '/manageCampaigns/sales?navSection=marketing'
            : '/manageCampaigns/sales'
        }
        icon={InviteIcon}
      >
        {isNewNav ? 'Automated invites' : 'Invite messages'} {inviteWarningIcon}
      </Link>
    )
  }

  function renderAutomatedTransactionalLink(navSection = 'marketing') {
    const to =
      isNewNav && navSection
        ? `/manageCampaigns/service?navSection=${navSection}`
        : '/manageCampaigns/service'

    return (
      <Link to={to} icon={InPropertyIcon}>
        {isNewNav ? 'Automated transactional' : 'In-property messages'}
      </Link>
    )
  }

  function renderAnnouncementsLink() {
    return (
      <Link to="/announcements" icon={AnnouncementIcon}>
        Announcements
      </Link>
    )
  }

  function renderDripCampaignsLink() {
    return (
      <Link
        to={isNewNav ? '/custom-campaigns?navSection=marketing' : '/custom-campaigns'}
        icon={TextBlastIcon}
      >
        {isNewNav ? 'Drip campaign' : 'Drip campaigns'}
      </Link>
    )
  }

  const canSeeCampaigns = canSeePage(userRoles.CampaignView) && !isResortNearDisney
  const canSeeMessagingPerformance = !!(
    (roles[userRoles.Billing] === userRoleLevels.HasGroupLevelPermission ||
      roles[userRoles.BookingsView] === userRoleLevels.HasGroupLevelPermission ||
      roles[userRoles.BookingsManage] === userRoleLevels.HasGroupLevelPermission ||
      roles[userRoles.UserManage] === userRoleLevels.HasGroupLevelPermission ||
      isInternalAdmin) &&
    !isIndependentlyManaged
  )
  const canSeeInPropertyGroup =
    !isResortNearDisney && (hasMessagingAccess || canSeeCampaigns)
  const canSeeGuestReviewsGroup =
    !isResortNearDisney &&
    (canSeePage(userRoles.ReportsView) || canSeePage(userRoles.ChatSalesAfter))

  const accountGroup = (
    <Group name="Account">
      {hasPepPMS && (
        <Link to="/pms-reports" icon={UploadReportIcon}>
          PMS Reports
        </Link>
      )}
      {canSeePage(userRoles.Billing) && (
        <Link to="/billing" icon={InvoiceIcon}>
          Billing and payments
        </Link>
      )}
      {canSeePage(userRoles.UserManage) && (
        <Link to="/userPermissions" icon={UserManagementIcon}>
          User Management
        </Link>
      )}
      <Link to="/compliance" icon={ComplianceIcon}>
        Compliance
      </Link>
      <Link to="/hotelDetails" icon="business-icon" adminOnly>
        Hotel details
      </Link>
      <Link to="/notificationPreferences" icon={NotificationIcon}>
        Configure notifications
      </Link>
    </Group>
  )

  const adminGroup = (
    <Group adminOnly name="Admin" onDoubleClick={() => history.push('/debugApp')}>
      <Link to="/addGuestData" icon="upload-data-icon" adminOnly>
        Add guest data
      </Link>
      {!isPreOnboardingCompleted && (
        <Link to="/hotel-setup" icon={HotelSetupIcon}>
          Hotel setup
        </Link>
      )}
      <Link to="/usage-stats" icon={UsageStatsIcon}>
        Usage stats
      </Link>
      <Link to="/occupancy" icon={OccupancyIcon}>
        Occupancy
      </Link>
      <Link to="/monthly-revenue" icon={MonthlyRevenue}>
        Monthly revenue
      </Link>
      <Link to="/monthly-ota-conversions" icon={MonthlyRevenue}>
        Monthly OTA conversions
      </Link>
      <Link to="/contracts" icon={ContractIcon}>
        Contracts
      </Link>
      <Link adminOnly to="/hotels" icon="hotels-icon">
        Hotels
      </Link>
      <Link adminOnly to="/hotels-by-csm" icon={CSMListIcon}>
        Manage CSM's
      </Link>
      <Link adminOnly to="/hotels-by-location" icon={MapIcon}>
        Hotels by location
      </Link>
      <Link to="/users" adminOnly icon="users-icon">
        Dashboard Users
      </Link>
      <Link to="/healthCheck/pms" icon={HealthIcon}>
        Health check
      </Link>
      <Link to="/defaultCampaigns/sales" icon={DefaultCampaignsIcon}>
        Default campaigns
      </Link>
      <Link to="/restricted-call-dates" icon={RestrictedDateIcon}>
        Restricted call dates
      </Link>
      <Link to="/emails" icon={EmailIcon}>
        Email logs
      </Link>
      <Link to="/dailyConversations" icon="ai-training-icon">
        Daily converations
      </Link>
      <Link to="/campaign-analysis" icon={CampaignAnalysisIcon}>
        Campaign analysis
      </Link>
      <Link to="/llm-platform" icon="ai-training-icon" adminOnly>
        LLM Platform
      </Link>
    </Group>
  )

  const oldNav = (
    <>
      <Group>
        {renderArrivalsLink()}
        {renderBookingsLinks()}
        {renderRecentSubscriptionsLink()}
        {renderSiteAbandonmentLink()}
        {renderOtaBusterLink()}
      </Group>
      {!isResortNearDisney && hasMessagingAccess && (
        <Group name="Guest messaging">
          {renderDuringStayLink()}
          {renderBeforeStayLink()}
          {renderGuestResponsesLink()}
          {renderDirectMessagesLink()}
          {renderChatbotLink()}
        </Group>
      )}{' '}
      <Group name="Reports">
        {renderAnalyticsLink()}
        {renderAnalyticsSubNav()}
        {renderRoiLink()}
        {renderReviewRatingsLink()}
        {renderMessagingPerformanceLink()}
      </Group>
      {canSeeCampaigns && (
        <Group name="Automated messages">
          {renderInviteMessagesLink()}
          {renderAutomatedTransactionalLink()}
          {renderAnnouncementsLink()}
          {renderDripCampaignsLink()}
        </Group>
      )}
      {accountGroup}
      {adminGroup}
    </>
  )

  const newNav = (
    <>
      <Group>
        {renderAnalyticsLink()}
        {renderAnalyticsSubNav()}
        {renderBookingsLinks()}
        {renderRecentSubscriptionsLink()}
      </Group>
      <Group name="Marketing">
        {canSeeCampaigns && renderInviteMessagesLink()}
        {canSeeCampaigns && renderAutomatedTransactionalLink('marketing')}
        {canSeeCampaigns && renderDripCampaignsLink()}
        {renderGuestResponsesLink()}
        {renderBeforeStayLink()}
        {renderSiteAbandonmentLink()}
        {renderOtaBusterLink()}
      </Group>
      {canSeeInPropertyGroup && (
        <Group name="In Property">
          {renderArrivalsLink()}
          {renderDuringStayLink()}
          {canSeeCampaigns && renderAnnouncementsLink()}
          {canSeeCampaigns && renderAutomatedTransactionalLink('inProperty')}
          {renderChatbotLink()}
          {renderDirectMessagesLink()}
        </Group>
      )}
      {canSeeGuestReviewsGroup && (
        <Group name="Guest Reviews">
          {renderReviewRatingsLink()}
          {renderGuestResponsesLink()}
        </Group>
      )}
      {(hasRoiAccess || canSeeMessagingPerformance) && (
        <Group name="Reports">
          {renderRoiLink()}
          {renderMessagingPerformanceLink()}
        </Group>
      )}
      {accountGroup}
      {adminGroup}
    </>
  )

  return (
    <>
      <div
        onMouseEnter={() => setMobileMenuOpen(true)}
        onMouseLeave={() => setMobileMenuOpen(false)}
        className={`main-sidebar ${
          noCollapse ? '' : 'main-sidebar-collapsable'
        } mobile-menu-${mobileMenuOpen}`}
      >
        <div className="sidebarContainer">
          <CurrentHotelWithSelector isMobileMenuOpen={mobileMenuOpen} />
          <div className="sidebarScrollContainer">
            <Group>
              <div className="sidebar-nav-mode-card">
                <div className="sidebar-nav-mode-card-header">
                  <span className="sidebar-nav-mode-card-title">
                    {isNewNav ? 'New navigation' : "You're in classic"}
                  </span>
                  <span className="sidebar-nav-mode-card-badge">
                    {isNewNav ? 'Active' : 'New'}
                  </span>
                </div>
                <p className="sidebar-nav-mode-card-text">
                  {isNewNav
                    ? "We've introduced a new sidebar experience. Prefer the previous layout?"
                    : 'The new navigation is here. Cleaner, simpler, and easier to navigate. Try it anytime. You can always switch back.'}
                </p>
                <button
                  type="button"
                  className="sidebar-nav-mode-card-button"
                  onClick={isNewNav ? handleSwitchToClassic : handleSwitchToNew}
                >
                  <svg
                    className="sidebar-nav-mode-card-button-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 8a4.5 4.5 0 0 1 7.6-3.3M12.5 8a4.5 4.5 0 0 1-7.6 3.3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M11.2 2.2v2.6H8.6M4.8 13.8v-2.6h2.6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {isNewNav ? 'Switch to classic' : 'Switch to new navigation'}
                </button>
              </div>
            </Group>
            {isNewNav ? newNav : oldNav}
            {/* {!isResortNearDisney && (
              <Group>
                <Link to="/downloadApp" icon={AddAppIcon}>
                  Download mobile app
                </Link>
              </Group>
            )} */}
            <Group>
              <div
                style={{
                  padding: '2px 30px 6px',
                  fontSize: 13,
                  color: 'gray',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SidebarDirectfulTestLogo />
                Dashboard version {localVersion}
              </div>
            </Group>
          </div>
        </div>
      </div>
    </>
  )
}

const Group = ({ name, children, adminOnly, onDoubleClick }) => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  if (!children) {
    return null
  }

  if (adminOnly && !isInternalAdmin) {
    return null
  }

  return (
    <div className="sidebar-group">
      {name && <h3 onDoubleClick={onDoubleClick}>{name.toUpperCase()}</h3>}
      <ul>{children}</ul>
    </div>
  )
}

export default SidebarFunc
